import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

import Link from "next/link";
import {
  ArrowSquareOutIcon,
  BookIcon,
  BookOpenIcon,
  ChartBarIcon,
  ChartPieIcon,
  CloudIcon,
  DiscordLogoIcon,
  FileTextIcon,
  KeyIcon,
  PlugChargingIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { Pencil, Settings, Trash2, Bot } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePathname } from "next/navigation";
import ProjectSelector from "./ProjectSelector";

export const Divider = () => <hr className="border-border mt-4 mb-3" />;

export const CategoryTitle = ({
  children,
  isCollapsed,
}: {
  children: React.ReactNode;
  isCollapsed: boolean;
}) => (
  <div
    className={cn(
      "items-center",
      isCollapsed ? "w-full max-h-1" : "flex px-spacing-1 gap-spacing-2",
    )}
  >
    {children}
    <div
      className={cn(
        "h-px bg-palette-border-container transition-all duration-300 ease-[cubic-bezier(0.36,0.01,0.22,1)]",
        isCollapsed ? "w-[36px] mx-auto" : "w-full",
      )}
    />
  </div>
);

export const SidebarCategory = ({
  title,
  items,
  isCollapsed,
}: {
  title: string;
  items: {
    name: string;
    href: string;
    icon: React.ElementType;
    externalLink?: boolean;
  }[];
  isCollapsed: boolean;
}) => {
  const pathname = usePathname();

  return (
    <div className="flex flex-col gap-spacing-3 cursor-pointer">
      <CategoryTitle isCollapsed={isCollapsed}>
        <p
          className={cn(
            "transition-all duration-300 ease-in-out inline-block whitespace-nowrap paragraphs-Small-Regular-Uppercase text-[10px] text-palette-text-secondary",
            isCollapsed
              ? "opacity-0 h-0 scale-90 w-0"
              : "opacity-100 scale-100 w-fit",
          )}
        >
          {title}
        </p>
      </CategoryTitle>

      <div className="flex flex-col">
        {title === "CURRENT PROJECT" && (
          <>
            <ProjectSelector isCollapsed={isCollapsed} />
            <div
              className={cn(
                "h-spacing-4 pr-3 pl-spacing-3 flex items-center justify-center",
                isCollapsed ? "w-[44px] min-w-[44px]" : "w-[209px]",
              )}
            >
              <div
                className={cn(
                  "h-px bg-palette-border-container",
                  isCollapsed ? "w-[16px] min-w-[16px]" : "w-[185px]",
                )}
              />
            </div>
          </>
        )}

        {items.map((item) => (
          <Link
            key={item.name}
            href={item.href}
            className={cn(
              "flex items-center justify-between h-spacing-8 rounded-sm gap-spacing-2 px-spacing-3 transition-colors",
              pathname === item.href
                ? "bg-palette-surface-container"
                : "hover:bg-palette-surface-container",
            )}
            target={item.externalLink ? "_blank" : undefined}
          >
            <div className="flex gap-spacing-3 items-center min-w-0">
              {isCollapsed ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <item.icon className="h-5 w-5 shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="ml-2" sideOffset={8}>
                    {item.name}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <item.icon className="h-5 w-5 shrink-0" />
              )}
              <span
                className={cn(
                  "pb-[2px] text-palette-text-primary text-sm font-medium font-sans transition-all duration-300 ease-in-out inline-block truncate",
                  isCollapsed
                    ? "opacity-0 scale-90 w-0"
                    : "opacity-100 scale-100 w-auto",
                )}
              >
                {item.name}
              </span>
            </div>
            {item.externalLink && (
              <ArrowSquareOutIcon
                width={16}
                height={16}
                className={cn(
                  "transition-all duration-300 ease-in-out shrink-0",
                  isCollapsed ? "opacity-0 scale-90" : "opacity-100 scale-100",
                )}
              />
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

export const categories = (currentProjectId: string) => [
  {
    title: "PLATFORM",
    items: [
      {
        name: "Getting Started",
        href: `/getting-started`,
        icon: BookOpenIcon,
      },
      {
        name: "Dashboard",
        href: `/dashboard`,
        icon: ChartBarIcon,
      },
      {
        name: "Logs",
        href: `/logs`,
        icon: FileTextIcon,
      },
    ],
  },
  {
    title: "CURRENT PROJECT",
    items: [
      {
        name: "AG-UI Agents",
        href: `/projects/${currentProjectId}/agents`,
        icon: Bot,
      },
      {
        name: "Remote Endpoints",
        href: `/projects/${currentProjectId}/remote-endpoints`,
        icon: CloudIcon,
      },
      {
        name: "API Key",
        href: `/projects/${currentProjectId}/api-key`,
        icon: KeyIcon,
      },
      {
        name: "License Key",
        href: `/projects/${currentProjectId}/license-key`,
        icon: KeyIcon,
      },
      {
        name: "Team",
        href: `/projects/${currentProjectId}/team`,
        icon: UsersIcon,
      },
      {
        name: "LLM Adapter",
        href: `/projects/${currentProjectId}/llms`,
        icon: PlugChargingIcon,
      },
      {
        name: "Analytics Integrations",
        href: `/projects/${currentProjectId}/analytics`,
        icon: ChartPieIcon,
      },
    ],
  },
  {
    title: "RESOURCES",
    items: [
      {
        name: "Documentation",
        href: "https://docs.copilotkit.ai",
        icon: BookIcon,
        externalLink: true,
      },
      {
        name: "Discord Community",
        href: "https://discord.com/invite/6dffbvGU3D",
        icon: DiscordLogoIcon,
        externalLink: true,
      },
    ],
  },
];

export const ProjectActions = ({
  projectId,
  onRename,
  onDelete,
}: {
  projectId: string;
  onRename: (e: Event, projectId: string) => void;
  onDelete: (e: Event, projectId: string) => void;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Settings className="h-4 w-4" />
          <span className="sr-only">Project Settings</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={5} align="start">
        <DropdownMenuItem onSelect={(e) => onRename(e, projectId)}>
          <Pencil className="mr-2 h-4 w-4" />
          <span>Rename</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
          onSelect={(e) => onDelete(e, projectId)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
